/**
 * @fileoverview WebSocket client for the Buzzer Battle Admin Operator Panel ("De barrio en barrio").
 * Connects to the platform's real-time communication channel as superadmin to receive
 * state snapshots, telemetry, answer selections, and emit host/operator commands.
 * 
 * @module utilities/WS/Admin/BuzzerBattleWS
 */

let buzzerBattleAdminWebSocket = null;
let isBuzzerBattleAdminConnected = false;
let reconnectionTimeoutIdentifier = null;
let connectionAttemptCounter = 0;
const MAXIMUM_RECONNECTION_ATTEMPTS = 15;
const RECONNECTION_DELAY_MILLISECONDS = 3000;

/**
 * Establishes a persistent WebSocket connection for the Buzzer Battle admin panel.
 * 
 * @function ConnectWebSocketForBuzzerBattleAdmin
 * @param {Function} [onMessageReceived=()=>{}] - Callback invoked whenever a message is received.
 * @param {Function} [onStatusChange=()=>{}] - Callback invoked when connection status changes ('connecting', 'connected', 'reconnecting', 'error', 'disconnected').
 */
export const ConnectWebSocketForBuzzerBattleAdmin = (
    onMessageReceived = () => {},
    onStatusChange = () => {}
) => {
    if (buzzerBattleAdminWebSocket || isBuzzerBattleAdminConnected) {
        return;
    }

    const establishConnection = () => {
        if (connectionAttemptCounter >= MAXIMUM_RECONNECTION_ATTEMPTS) {
            onStatusChange('error');
            return;
        }

        connectionAttemptCounter += 1;
        onStatusChange('connecting');

        try {
            const webSocketEndpointUrl = `${process.env.REACT_APP_API_URL_WSS}?tipo=superadmin`;
            buzzerBattleAdminWebSocket = new WebSocket(webSocketEndpointUrl);

            buzzerBattleAdminWebSocket.onopen = () => {
                isBuzzerBattleAdminConnected = true;
                connectionAttemptCounter = 0;
                onStatusChange('connected');

                // Request initial snapshot upon successful connection
                RequestBuzzerBattleState();
            };

            buzzerBattleAdminWebSocket.onclose = () => {
                isBuzzerBattleAdminConnected = false;
                buzzerBattleAdminWebSocket = null;
                onStatusChange('reconnecting');

                clearTimeout(reconnectionTimeoutIdentifier);
                reconnectionTimeoutIdentifier = setTimeout(establishConnection, RECONNECTION_DELAY_MILLISECONDS);
            };

            buzzerBattleAdminWebSocket.onerror = () => {
                onStatusChange('error');
            };

            buzzerBattleAdminWebSocket.onmessage = (messageEvent) => {
                try {
                    const parsedPayload = JSON.parse(messageEvent.data);
                    onMessageReceived(parsedPayload);
                } catch (parseError) {
                    // Ignore non-JSON or unformatted packets
                }
            };
        } catch (connectionError) {
            onStatusChange('error');
            clearTimeout(reconnectionTimeoutIdentifier);
            reconnectionTimeoutIdentifier = setTimeout(establishConnection, RECONNECTION_DELAY_MILLISECONDS);
        }
    };

    establishConnection();
};

/**
 * Sends a JSON payload through the active Buzzer Battle admin WebSocket connection.
 * 
 * @function SendBuzzerBattleAdminMessage
 * @param {Object} payloadObject - JSON object to transmit.
 * @returns {boolean} True if successfully sent, false otherwise.
 */
export const SendBuzzerBattleAdminMessage = (payloadObject) => {
    try {
        if (buzzerBattleAdminWebSocket && buzzerBattleAdminWebSocket.readyState === WebSocket.OPEN) {
            buzzerBattleAdminWebSocket.send(JSON.stringify(payloadObject));
            return true;
        }
        return false;
    } catch (sendError) {
        return false;
    }
};

/**
 * Closes and cleans up the Buzzer Battle admin WebSocket connection.
 * 
 * @function DisconnectWebSocketForBuzzerBattleAdmin
 */
export const DisconnectWebSocketForBuzzerBattleAdmin = () => {
    try {
        if (reconnectionTimeoutIdentifier) {
            clearTimeout(reconnectionTimeoutIdentifier);
            reconnectionTimeoutIdentifier = null;
        }

        if (buzzerBattleAdminWebSocket) {
            buzzerBattleAdminWebSocket.onopen = null;
            buzzerBattleAdminWebSocket.onclose = null;
            buzzerBattleAdminWebSocket.onerror = null;
            buzzerBattleAdminWebSocket.onmessage = null;

            if (buzzerBattleAdminWebSocket.readyState === WebSocket.OPEN) {
                buzzerBattleAdminWebSocket.close();
            } else if (buzzerBattleAdminWebSocket.readyState === WebSocket.CONNECTING) {
                const socketToClose = buzzerBattleAdminWebSocket;
                socketToClose.onopen = () => {
                    try {
                        socketToClose.close();
                    } catch {
                        // Safe ignore
                    }
                };
            }

            buzzerBattleAdminWebSocket = null;
            isBuzzerBattleAdminConnected = false;
            connectionAttemptCounter = 0;
        }
    } catch (teardownError) {
        // Safe teardown
    }
};

// =============================================================================
// TYPED ACTION HELPERS FOR OPERATOR COMMANDS
// =============================================================================

/**
 * Requests the current synchronized game state snapshot from the server.
 * 
 * @function RequestBuzzerBattleState
 * @returns {boolean} True if sent successfully.
 */
export const RequestBuzzerBattleState = () => {
    return SendBuzzerBattleAdminMessage({
        type: 'buzzer_get_state'
    });
};

/**
 * Emits command to project the cover promotional screen.
 * 
 * @function SendBuzzerShowCover
 * @returns {boolean} True if sent successfully.
 */
export const SendBuzzerShowCover = () => {
    return SendBuzzerBattleAdminMessage({
        type: 'buzzer_show_cover'
    });
};

/**
 * Emits command to project the interactive combat arena HUD screen.
 * 
 * @function SendBuzzerShowGame
 * @returns {boolean} True if sent successfully.
 */
export const SendBuzzerShowGame = () => {
    return SendBuzzerBattleAdminMessage({
        type: 'buzzer_show_game'
    });
};

/**
 * Emits command to initialize a new match with theme, question set, and custom players.
 * 
 * @function SendBuzzerInitMatch
 * @param {number} themeIdentifier - Theme ID.
 * @param {number} questionSetIdentifier - Question set ID.
 * @param {Array<Object>|null} [customPlayers=null] - Optional custom players list.
 * @returns {boolean} True if sent successfully.
 */
export const SendBuzzerInitMatch = (themeIdentifier, questionSetIdentifier, customPlayers = null) => {
    return SendBuzzerBattleAdminMessage({
        type: 'buzzer_init_match',
        themeId: Number(themeIdentifier),
        questionSetId: Number(questionSetIdentifier),
        customPlayers: Array.isArray(customPlayers) ? customPlayers : null,
        data: {
            themeId: Number(themeIdentifier),
            questionSetId: Number(questionSetIdentifier),
            customPlayers: Array.isArray(customPlayers) ? customPlayers : null
        }
    });
};

/**
 * Emits command to launch an active question or a specific question index.
 * 
 * @function SendBuzzerLaunchQuestion
 * @param {number|null} [questionIndex=null] - Zero-based question index.
 * @returns {boolean} True if sent successfully.
 */
export const SendBuzzerLaunchQuestion = (questionIndex = null) => {
    const sanitizedIndex = (questionIndex !== null && questionIndex !== undefined && !isNaN(Number(questionIndex)))
        ? Number(questionIndex)
        : null;

    return SendBuzzerBattleAdminMessage({
        type: 'buzzer_launch_question',
        questionIndex: sanitizedIndex,
        data: {
            questionIndex: sanitizedIndex
        }
    });
};

/**
 * Emits command to display answer options on screen and mobile pulsers.
 * 
 * @function SendBuzzerShowOptions
 * @returns {boolean} True if sent successfully.
 */
export const SendBuzzerShowOptions = () => {
    return SendBuzzerBattleAdminMessage({
        type: 'buzzer_show_options'
    });
};

/**
 * Emits command to assign answering turn to a specific player.
 * 
 * @function SendBuzzerAssignTurn
 * @param {string} playerIdentifier - Player ID / keycode.
 * @returns {boolean} True if sent successfully.
 */
export const SendBuzzerAssignTurn = (playerIdentifier) => {
    return SendBuzzerBattleAdminMessage({
        type: 'buzzer_assign_turn',
        playerId: playerIdentifier,
        data: {
            playerId: playerIdentifier
        }
    });
};

/**
 * Emits command to manually select an option from the admin operator panel.
 * 
 * @function SendBuzzerManualSelectOption
 * @param {number} optionIdentifier - Selected option ID.
 * @returns {boolean} True if sent successfully.
 */
export const SendBuzzerManualSelectOption = (optionIdentifier) => {
    return SendBuzzerBattleAdminMessage({
        type: 'buzzer_manual_select_option',
        optionId: Number(optionIdentifier),
        data: {
            optionId: Number(optionIdentifier)
        }
    });
};

/**
 * Emits command to clear selected answer before verdict reveal.
 * 
 * @function SendBuzzerClearOption
 * @returns {boolean} True if sent successfully.
 */
export const SendBuzzerClearOption = () => {
    return SendBuzzerBattleAdminMessage({
        type: 'buzzer_clear_option'
    });
};

/**
 * Emits command to reveal answer correctness and update scores.
 * 
 * @function SendBuzzerRevealResult
 * @returns {boolean} True if sent successfully.
 */
export const SendBuzzerRevealResult = () => {
    return SendBuzzerBattleAdminMessage({
        type: 'buzzer_reveal_result'
    });
};

/**
 * Emits command to grant rebound to another player.
 * 
 * @function SendBuzzerRebound
 * @param {string} nextPlayerIdentifier - Player ID receiving rebound.
 * @returns {boolean} True if sent successfully.
 */
export const SendBuzzerRebound = (nextPlayerIdentifier) => {
    return SendBuzzerBattleAdminMessage({
        type: 'buzzer_rebound',
        nextPlayerId: nextPlayerIdentifier,
        data: {
            nextPlayerId: nextPlayerIdentifier
        }
    });
};

/**
 * Emits command to advance to next question or show final results.
 * 
 * @function SendBuzzerNextQuestion
 * @returns {boolean} True if sent successfully.
 */
export const SendBuzzerNextQuestion = () => {
    return SendBuzzerBattleAdminMessage({
        type: 'buzzer_next_question'
    });
};

/**
 * Emits command to rewind to previous question.
 * 
 * @function SendBuzzerPreviousQuestion
 * @returns {boolean} True if sent successfully.
 */
export const SendBuzzerPreviousQuestion = () => {
    return SendBuzzerBattleAdminMessage({
        type: 'buzzer_previous_question'
    });
};

/**
 * Emits command to adjust a player's score (+1 or -1).
 * 
 * @function SendBuzzerAdjustPlayerScore
 * @param {string} playerIdentifier - Player ID (e.g. 'J1', 'J2').
 * @param {number} pointsDelta - Points delta (+1 or -1).
 * @returns {boolean} True if sent successfully.
 */
export const SendBuzzerAdjustPlayerScore = (playerIdentifier, pointsDelta) => {
    return SendBuzzerBattleAdminMessage({
        type: 'buzzer_adjust_player_score',
        playerId: playerIdentifier,
        delta: pointsDelta,
        data: {
            playerId: playerIdentifier,
            delta: pointsDelta
        }
    });
};

/**
 * Emits command to show podium and final results.
 * 
 * @function SendBuzzerShowFinalResults
 * @returns {boolean} True if sent successfully.
 */
export const SendBuzzerShowFinalResults = () => {
    return SendBuzzerBattleAdminMessage({
        type: 'buzzer_show_final_results'
    });
};

/**
 * Emits command to reset match back to idle state.
 * 
 * @function SendBuzzerResetMatch
 * @returns {boolean} True if sent successfully.
 */
export const SendBuzzerResetMatch = () => {
    return SendBuzzerBattleAdminMessage({
        type: 'buzzer_reset'
    });
};

/**
 * Emits command to force current answer as correct.
 * 
 * @function SendBuzzerForceCorrect
 * @returns {boolean} True if sent successfully.
 */
export const SendBuzzerForceCorrect = () => {
    return SendBuzzerBattleAdminMessage({
        type: 'buzzer_force_correct'
    });
};

/**
 * Emits command to force current answer as incorrect (strike).
 * 
 * @function SendBuzzerForceIncorrect
 * @returns {boolean} True if sent successfully.
 */
export const SendBuzzerForceIncorrect = () => {
    return SendBuzzerBattleAdminMessage({
        type: 'buzzer_force_incorrect'
    });
};
